import { useCallback, useRef } from 'react';

import { Table } from '@tanstack/react-table';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { GenerateEntityRefreshButtonProps } from '../components';

interface UseGenerateRefreshArgs<TItem, TFormValues extends FieldValues> {
  form: UseFormReturn<TFormValues>;
  table: Table<TItem>;
  hasStaleData: boolean;
  isRefreshing: boolean;
  isFetching: boolean;
  dataUpdatedAt: number;
  applySmartRefresh: (form: UseFormReturn<TFormValues>, table: Table<TItem>) => Promise<void>;
}

interface UseGenerateRefreshReturn {
  handleRefresh: () => void;
  refreshButtonProps: GenerateEntityRefreshButtonProps;
}

/**
 * Wires up the refresh button: keeps stable refs to form/table so the
 * onClick callback identity doesn't change every render, and assembles the
 * fully-translated `refreshButtonProps` shape the template expects.
 */
export const useGenerateRefresh = <TItem, TFormValues extends FieldValues>({
  form,
  table,
  hasStaleData,
  isRefreshing,
  isFetching,
  dataUpdatedAt,
  applySmartRefresh,
}: UseGenerateRefreshArgs<TItem, TFormValues>): UseGenerateRefreshReturn => {
  const { t: tCommon } = useTranslation('common');

  const formRef = useRef(form);
  formRef.current = form;
  const tableRef = useRef(table);
  tableRef.current = table;

  const handleRefresh = useCallback(() => {
    void applySmartRefresh(formRef.current, tableRef.current);
  }, [applySmartRefresh]);

  const refreshButtonProps: GenerateEntityRefreshButtonProps = {
    hasStaleData,
    isRefreshing: isRefreshing || isFetching,
    dataUpdatedAt,
    onRefresh: handleRefresh,
    tooltipDescription: tCommon('generateEntities.staleDataTooltip'),
    refreshingLabel: tCommon('generateEntities.refreshing'),
    label: tCommon('generateEntities.refreshLabel'),
    successLabel: tCommon('generateEntities.refreshSuccess'),
  };

  return { handleRefresh, refreshButtonProps };
};
